import type { MannequinMotionConfig } from "../render/types.ts";

/**
 * Small presentation-only separation between authored soles and the rendered
 * ground. This is not added to simulation or physics positions.
 */
export const CHARACTER_PRESENTATION_CONFIG = Object.freeze({
  soleClearanceMeters: 0.008,
  /** Keeps the storybook mannequin inside the authored compact-car canopy. */
  vehicleSeatedHeightScale: 0.78,
  /** Preserves the hip-to-cushion contact after seated height compression. */
  vehicleSeatedRootOffsetMeters: -0.69,
  /** PROVISIONAL POST-M5 CHARACTER ART VALUES — USER REVIEW REQUIRED. */
  proportions: Object.freeze({
    headScale: 1.1,
    legWidthScale: 1.12,
    bootScale: 1.28,
    shoulderJointOffsetMeters: 0.27,
    sleeveCapRadiusScale: 0.85,
    shoulderRollRadialSegments: 8,
  }),
  armRestSplayRadians: 0.105,
  elderStoopRadians: 0.105,
  inspection: Object.freeze({
    bodyDistanceMeters: 3.15,
    bodyTargetHeightOffsetMeters: 0.12,
    faceDistanceMeters: 1.5,
    faceTargetHeightOffsetMeters: 0.48,
  }),
  nightFill: Object.freeze({
    colour: 0xc9d6cc,
    duskIntensity: 0.14,
    nightIntensity: 0.72,
    distanceMeters: 5.5,
  }),
});

/**
 * PROVISIONAL M3 VALUES — USER TUNING REQUIRED.
 *
 * These values make each grounded/airborne state readable for verification.
 * They have not received a character-motion or movement-feel tuning pass.
 */
export const CHARACTER_MOTION_CONFIG: Readonly<MannequinMotionConfig> =
  Object.freeze({
    idleCycleHz: 0.18,
    idleSwayRadians: 0.025,
    idleWeightShiftMeters: 0.028,
    idleBreathMeters: 0.009,
    walkStrideMeters: 1.4,
    walkSwingRadians: 0.45,
    walkBobMeters: 0.018,
    runStrideMeters: 1.8,
    runSwingRadians: 0.78,
    runBobMeters: 0.036,
    walkLeanRadians: 0.035,
    runLeanRadians: 0.085,
    airborneArmRadians: 0.35,
    jumpLegRadians: 0.28,
    fallLegRadians: 0.18,
    locomotionThresholdMetersPerSecond: 0.2,
    verticalStateThresholdMetersPerSecond: 0.15,
  });
